import React from "react";
import styled, { keyframes, css } from "styled-components";
import { FiCalendar, FiClock, FiMapPin } from "react-icons/fi";
import {
  TOP_WINNERS,
  OTHER_WINNERS,
  PRIZE_CLAIM_EVENTS,
} from "../data/WinnersData";

const Section = styled.section`
  position: relative;
  padding: 5rem 1.5rem 6rem;
  background:
    radial-gradient(circle at 12% 18%, rgba(255, 206, 45, 0.5) 0%, transparent 42%),
    radial-gradient(circle at 88% 22%, rgba(61, 165, 232, 0.35) 0%, transparent 48%),
    linear-gradient(180deg, #fff8e1 0%, #ffeec2 100%);
  overflow: hidden;
`;

const Wrap = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 2;
`;

const Eyebrow = styled.p`
  text-transform: uppercase;
  letter-spacing: 0.18em;
  font-size: 0.8rem;
  color: #faa71e;
  font-weight: 700;
  margin-bottom: 0.75rem;
`;

const Heading = styled.h2`
  font-size: clamp(1.8rem, 5vw, 3rem);
  font-weight: 800;
  color: #0a2a4a;
  line-height: 1.1;
  margin: 0 auto 0.75rem;
  max-width: 800px;
`;

const Intro = styled.p`
  font-size: clamp(1rem, 2.4vw, 1.2rem);
  color: #0a2a4a;
  opacity: 0.85;
  max-width: 680px;
  margin: 0 auto 3rem;
  line-height: 1.55;
`;

const Podium = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: stretch;
  gap: 1.25rem;
  margin-bottom: 3rem;
`;

const shine = keyframes`
  0%, 100% { box-shadow: 0 18px 36px -16px rgba(250, 167, 30, 0.55); }
  50% { box-shadow: 0 22px 44px -14px rgba(250, 167, 30, 0.8); }
`;

const PodiumCard = styled.div`
  flex: 1 1 220px;
  max-width: 280px;
  background: #fff;
  border-radius: 22px;
  padding: 1.75rem 1.25rem;
  border: 2px solid
    ${({ rank }) =>
      rank === 0 ? "#ffce2d" : rank === 1 ? "#cfd8e3" : "#e6b98a"};
  box-shadow: 0 16px 32px -18px rgba(10, 42, 74, 0.3);
  position: relative;

  ${({ rank }) =>
    rank === 0 &&
    css`
      animation: ${shine} 3s ease-in-out infinite;
    `}
`;

const Medal = styled.div`
  font-size: 2.4rem;
  line-height: 1;
  margin-bottom: 0.5rem;
`;

const Place = styled.div`
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 0.8rem;
  font-weight: 700;
  color: #0a2a4a;
  opacity: 0.7;
  margin-bottom: 0.4rem;
`;

const TopNumber = styled.div`
  font-size: clamp(2.4rem, 7vw, 3.4rem);
  font-weight: 800;
  color: #faa71e;
  line-height: 1;
  font-variant-numeric: tabular-nums;
`;

const SubHeading = styled.h3`
  font-size: 1.05rem;
  font-weight: 700;
  color: #0a2a4a;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 1.25rem;
  opacity: 0.85;
`;

const NumberGrid = styled.ul`
  list-style: none;
  margin: 0 auto 3.5rem;
  padding: 0;
  max-width: 760px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  gap: 0.75rem;
`;

const NumberChip = styled.li`
  background: #fff;
  border: 1px solid rgba(10, 42, 74, 0.1);
  border-radius: 14px;
  padding: 0.7rem 0.5rem;
  font-weight: 800;
  font-size: 1.15rem;
  color: #0a2a4a;
  font-variant-numeric: tabular-nums;
  box-shadow: 0 8px 18px -12px rgba(10, 42, 74, 0.25);

  span {
    display: block;
    font-size: 0.65rem;
    font-weight: 600;
    color: #faa71e;
    letter-spacing: 0.08em;
    margin-bottom: 0.15rem;
  }
`;

const ClaimCard = styled.div`
  background: linear-gradient(180deg, #0a2a4a 0%, #133b66 100%);
  color: #fff;
  border-radius: 24px;
  padding: 2.5rem 1.75rem;
  max-width: 820px;
  margin: 0 auto;
  text-align: left;
  box-shadow: 0 30px 60px -24px rgba(10, 42, 74, 0.45);
`;

const ClaimTitle = styled.h3`
  font-size: clamp(1.3rem, 3.5vw, 1.7rem);
  font-weight: 800;
  text-align: center;
  margin-bottom: 0.5rem;
`;

const ClaimLead = styled.p`
  text-align: center;
  opacity: 0.9;
  margin-bottom: 2rem;
  line-height: 1.5;
`;

const Events = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 1.25rem;
  align-items: stretch;

  @media screen and (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const EventBox = styled.div`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 206, 45, 0.25);
  border-radius: 16px;
  padding: 1.5rem 1.25rem;
`;

const EventRow = styled.p`
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  margin: 0 0 0.75rem;
  line-height: 1.4;
  font-size: 0.95rem;

  &:last-child {
    margin-bottom: 0;
  }

  svg {
    flex-shrink: 0;
    margin-top: 2px;
    color: #ffce2d;
  }
`;

const Or = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #ffce2d;
  font-size: 0.85rem;

  @media screen and (max-width: 640px) {
    padding: 0.25rem 0;
  }
`;

const Note = styled.p`
  text-align: center;
  margin: 2rem 0 0;
  font-size: 0.95rem;
  opacity: 0.9;
  line-height: 1.5;
`;

const Closing = styled.p`
  text-align: center;
  margin: 2.5rem auto 0;
  max-width: 680px;
  font-size: clamp(1rem, 2.4vw, 1.15rem);
  font-weight: 600;
  color: #0a2a4a;
  line-height: 1.55;
`;

const MEDALS = ["🥇", "🥈", "🥉"];

const Winners = () => {
  return (
    <Section id="castigatori">
      <Wrap>
        <Eyebrow>Rezultate · Ediția 2026</Eyebrow>
        <Heading>🏆 Rățuștele câștigătoare sunt:</Heading>
        <Intro>
          Ai câștigat unul dintre premiile de la Cursa Rățuștelor? Verifică
          numărul de pe biletul tău în lista de mai jos.
        </Intro>

        <Podium>
          {TOP_WINNERS.map((w, i) => (
            <PodiumCard key={w.place} rank={i}>
              <Medal>{MEDALS[i]}</Medal>
              <Place>{w.place}</Place>
              <TopNumber>{w.number}</TopNumber>
            </PodiumCard>
          ))}
        </Podium>

        <SubHeading>Celelalte rățuște câștigătoare</SubHeading>
        <NumberGrid>
          {OTHER_WINNERS.map((number, i) => (
            <NumberChip key={number + i}>
              <span>Nr. {i + 4}</span>
              {number}
            </NumberChip>
          ))}
        </NumberGrid>

        <ClaimCard>
          <ClaimTitle>Cum revendici premiul</ClaimTitle>
          <ClaimLead>
            Premiile pentru rățuștele câștigătoare pot fi revendicate în 2
            momente:
          </ClaimLead>

          <Events>
            <EventBox>
              {PRIZE_CLAIM_EVENTS[0] && (
                <>
                  <EventRow>
                    <FiCalendar size={18} />
                    {PRIZE_CLAIM_EVENTS[0].date}
                  </EventRow>
                  <EventRow>
                    <FiClock size={18} />
                    {PRIZE_CLAIM_EVENTS[0].time}
                  </EventRow>
                  <EventRow>
                    <FiMapPin size={18} />
                    {PRIZE_CLAIM_EVENTS[0].location}
                  </EventRow>
                </>
              )}
            </EventBox>

            <Or>sau</Or>

            <EventBox>
              {PRIZE_CLAIM_EVENTS[1] && (
                <>
                  <EventRow>
                    <FiCalendar size={18} />
                    {PRIZE_CLAIM_EVENTS[1].date}
                  </EventRow>
                  <EventRow>
                    <FiClock size={18} />
                    {PRIZE_CLAIM_EVENTS[1].time}
                  </EventRow>
                  <EventRow>
                    <FiMapPin size={18} />
                    {PRIZE_CLAIM_EVENTS[1].location}
                  </EventRow>
                </>
              )}
            </EventBox>
          </Events>

          <Note>
            Te rugăm să ai la tine biletul câștigător, pentru validarea
            premiului.
          </Note>
        </ClaimCard>

        <Closing>
          Felicitări tuturor câștigătorilor și vă mulțumim că ați fost alături
          de noi la Cursa Rățuștelor! 🦆💛
          <br />
          Împreună facem valuri de bine! ✨
        </Closing>
      </Wrap>
    </Section>
  );
};

export default Winners;
